import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { homepageFAQs } from '../data/faqs';
import { scrollToElement } from '../utils/scrollUtils';
import { socialLinksForStructuredData } from '../data/socialLinks';
import { useContactInfo } from '../utils/contactObfuscation';

const Home = () => {
  const contactInfo = useContactInfo();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(true);
  const [testimonialScrollPosition, setTestimonialScrollPosition] = useState(0);
  const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);
  const navigate = useNavigate();
  const topServicesScrollRef = useRef(null);

  const [topServicesAtEnd, setTopServicesAtEnd] = useState(false);

  const updateTopServicesScrollState = useCallback(() => {
    const el = topServicesScrollRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    setTopServicesAtEnd(atEnd);
  }, []);



  // Helper functions for button actions
  const scrollToSection = async (sectionId) => {
    await scrollToElement(sectionId, { 
      smooth: true,
      maxRetries: 15,
      retryDelay: 150
    }).catch(() => {
      // Silent fail - scrollToElement handles retries internally
    });
  };

  const navigateToPage = (path) => {
    navigate(path);
  };


  const handleContactForm = () => {
    scrollToSection('contact');
  };


  const stats = [
    { number: '25+', label: 'AI & Engineering Specialists' },
    { number: '8+', label: 'AI & Product Launches' },
    { number: '4+', label: 'Years Shipping AI Systems' },
    { number: '120L+', label: 'Lines Of Production Code' },
    { number: '55+', label: 'Teams Trusting Our AI Delivery' },
    { number: '35+', label: 'AI & Cloud Technologies' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      jobTitle: 'CTO',
      company: 'TechFlow Solutions',
      companyDescription: 'AI support and ops platform',
      rating: 5,
      description: 'OctalChip shipped a production RAG assistant with citations and guardrails. Support resolution got dramatically faster.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'TechFlow'
    },
    {
      name: 'Michael Chen',
      jobTitle: 'Product Manager',
      company: 'InnovateCorp',
      companyDescription: 'AI-powered innovation platform',
      rating: 5,
      description: 'They built tool-using AI agents that automated lead qualification and cut repetitive ops work across our stack.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'InnovateCorp'
    },
    {
      name: 'Emily Rodriguez',
      jobTitle: 'CEO',
      company: 'StartupHub',
      companyDescription: 'The startup accelerator',
      rating: 5,
      description: 'OctalChip turned our GenAI idea into a measurable AI product roadmap and a working copilot MVP in weeks.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'StartupHub'
    },
    {
      name: 'David Thompson',
      jobTitle: 'Technical Director',
      company: 'Digital Dynamics',
      companyDescription: 'Enterprise AI operations',
      rating: 5,
      description: 'Their LLMOps setup (evals, monitoring, and cost controls) made our generative AI features production-ready.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'Digital Dynamics'
    },
    {
      name: 'Lisa Wang',
      jobTitle: 'VP Engineering',
      company: 'CloudScale Inc',
      companyDescription: 'Enterprise AI cloud systems',
      rating: 5,
      description: 'They integrated LLM workflows into our cloud stack securely and helped us scale AI features with confidence.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'CloudScale'
    }
  ];

  // Infinite scroll animation with hover pause (testimonials - same pattern used for Top Services / Top Stories)
  useEffect(() => {
    if (!isTestimonialPaused) {
      const interval = setInterval(() => {
        setTestimonialScrollPosition((prev) => {
          const cardWidth = 400; // Width of each testimonial card
          const totalWidth = testimonials.length * cardWidth; // Total width of all cards
          return prev >= totalWidth ? 0 : prev + 1; // Move 1px every frame for smooth animation
        });
      }, 16); // 60fps animation (1000ms / 60fps = ~16ms)

      return () => clearInterval(interval);
    }
  }, [testimonials.length, isTestimonialPaused]);

  useEffect(() => {
    const run = () => updateTopServicesScrollState();
    run();
    const t = setTimeout(run, 150);
    const el = topServicesScrollRef.current;
    if (el) el.addEventListener('scroll', updateTopServicesScrollState);
    window.addEventListener('resize', updateTopServicesScrollState);
    return () => {
      clearTimeout(t);
      const scrollEl = topServicesScrollRef.current;
      if (scrollEl) scrollEl.removeEventListener('scroll', updateTopServicesScrollState);
      window.removeEventListener('resize', updateTopServicesScrollState);
    };
  }, [updateTopServicesScrollState]);

  // Load recent blogs - Optimized to load all blog modules in parallel instead of sequentially
  useEffect(() => {
    const loadRecentBlogs = async () => {
      try {
        setIsLoadingBlogs(true);

        // Use Vite's import.meta.glob to dynamically import all blog files
        const blogModules = import.meta.glob('../blogs/*.jsx');
        const blogPaths = Object.keys(blogModules);

        // Load all blog files in parallel using Promise.all (much faster than sequential loading)
        const blogPromises = blogPaths.map(async (path) => {
          try {
            const module = await blogModules[path]();
            // Get the exported blog object (assuming each file exports one blog object)
            const blogData = Object.values(module)[0];
            if (blogData && blogData.slug && blogData.title) {
              return blogData;
            } else {
              console.warn(`Invalid blog data in ${path}:`, blogData);
              return null;
            }
          } catch (error) {
            console.error(`Error loading blog from ${path}:`, error);
            return null;
          }
        });

        // Wait for all blog modules to load in parallel
        const blogResults = await Promise.all(blogPromises);
        
        // Filter out any null results and sort blogs by publish date (newest first)
        const blogs = blogResults.filter(blog => blog !== null);
        blogs.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
        
        // Take only the first 3 most recent blogs
        const recentThreeBlogs = blogs.slice(0, 3);

        setRecentBlogs(recentThreeBlogs);
      } catch (error) {
        console.error('Error loading recent blogs:', error);
        setRecentBlogs([]);
      } finally {
        setIsLoadingBlogs(false);
      }
    };

    loadRecentBlogs();
  }, []);

  // ScrollToTop component handles hash-based scrolling globally

  // Helper function to format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Enhanced SEO Data
  const seoData = {
    title: 'AI Agents, Generative AI & LLM Solutions | OctalChip',
    description: 'OctalChip is an AI engineering company building generative AI apps, AI agents, RAG knowledge systems, and LLM-powered automation for growth-focused teams.',
    keywords: 'AI company, generative AI, AI agents, LLM development, RAG systems, enterprise AI, AI copilots, OpenAI, LangChain, vector databases, LLMOps, AI automation, machine learning, NLP, computer vision, AI chatbots, AI consulting, multimodal AI, private LLM, AI integration, OctalChip',
    canonical: 'https://www.octalchip.com/',
    ogImage: 'https://www.octalchip.com/og-image.png',

    tags: [
      'mobile',
      'development',
      'web',
      'app',
      'octalchip',
    ],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' },
    ],
    breadcrumbs: [{ name: 'Home', url: 'https://www.octalchip.com/' }],

    // GEO-specific data
    geoData: {
      region: "IN-GJ",
      placename: "Gujarat, India",
      latitude: "23.0225",
      longitude: "72.5714"
    },
    localBusinessData: true,
    serviceArea: [
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "France" },
      { "@type": "Country", "name": "Netherlands" },
      { "@type": "Country", "name": "Canada" },
      { "@type": "Country", "name": "Australia" },
      { "@type": "Country", "name": "UAE" },
      { "@type": "Country", "name": "Singapore" },
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "Saudi Arabia" },
      { "@type": "Country", "name": "Mexico" },
      { "@type": "Country", "name": "Brazil" },
      { "@type": "Country", "name": "Japan" },
      { "@type": "Country", "name": "South Korea" },
      { "@type": "Country", "name": "Poland" },
      { "@type": "Country", "name": "Romania" },
      { "@type": "Country", "name": "Bulgaria" }
    ],

    // AEO-specific data
    faqData: homepageFAQs,

    // Voice search optimization
    voiceSearchKeywords: [
      "AI engineering company",
      "mobile app AI engineering services",
      "AI development solutions",
      "cloud services provider",
      "DevOps consulting",
      "AI engineering company",
      "generative AI engineering services",
      "custom application development",
      "technology engineering company",
      "generative AI and AI agent development",
      "enterprise technology solutions",
      "AI product AI engineering company",
      "AI product UX design services",
      "backend AI engineering services",
      "machine learning development",
      "cloud migration services",
      "API AI engineering services",
      "workflow automation services",
      "technology consulting firm",
      "AI engineering outsourcing company",
      "hire AI engineers",
      "hire dedicated developers",
      "affordable AI-native web development",
      "AI engineering company India",
      "MVP AI engineering services",
      "24/7 development support",
      "application maintenance services",
      "fintech AI engineering company",
      "hourly development rates",
      "fixed-price development projects",
      "low-code development",
      "no-code development",
      "headless CMS development",
      "composable architecture",
      "MACH architecture",
      "GraphQL development",
      "test automation services",
      "E2E testing services",
      "design system development",
      "component library development",
      "application observability",
      "APM services",
      "real-time application development",
      "WebSocket development",
      "event-driven architecture",
      "WCAG compliance services",
      "accessibility audit services",
      "inclusive design services"
    ],
    conversationalQueries: [
      "How can I find a good AI engineering company?",
      "What is the cost of AI-powered mobile app development?",
      "Who provides AI engineering services?",
      "Where can I get cloud services?",
      "How to choose an AI engineering partner?",
      "What is the best AI agency for startups?",
      "How much does custom application development cost?",
      "What generative AI services does an AI company offer?",
      "Where can I find expert AI and machine learning developers?",
      "What is the difference between AI-native web development and AI-powered mobile app development?",
      "How long does it take to develop a AI products application?",
      "What technologies are used for cloud migration?",
      "How do I choose between native and cross-platform mobile development?",
      "What is included in generative AI product development?",
      "Where can I hire dedicated developers?",
      "What is the cost of AI development?",
      "How much does an AI agent pilot cost?",
      "Which is the best AI engineering company in India?",
      "Do you provide ongoing LLMOps support?",
      "How to hire AI engineers remotely?",
      "What are affordable generative AI options?",
      "How long does it take to build a RAG assistant?",
      "What is RAG vs fine-tuning?",
      "What is an AI agent vs a chatbot?",
      "How do you evaluate LLM quality?",
      "What is LLMOps?",
      "How do you prevent AI hallucinations?",
      "Which LLM providers do you use?",
      "How do you secure enterprise AI systems?",
      "What is AI workflow automation?"
    ],

    // Featured snippet optimization
    featuredSnippetContent: "OctalChip is an AI engineering company specializing in generative AI applications, AI agents, RAG knowledge systems, LLMOps, copilots, and AI-native products. With 25+ AI and engineering specialists, we help startups and enterprises ship production AI with evals, guardrails, and measurable ROI. We have delivered 8+ projects with 4+ years of experience serving clients worldwide.",

    // List data for featured snippets
    listData: {
      name: "OctalChip AI Engineering Services",
      description: "Generative AI, agents, RAG, and intelligent product solutions from OctalChip",
      items: [
        {
          name: "Generative AI & LLM Apps",
          description: "Production GenAI products with OpenAI, Claude, Gemini, evals, and secure deployment",
          url: "https://www.octalchip.com/services/ai-integration"
        },
        {
          name: "AI Agents & Copilots",
          description: "Tool-using agents and copilots that complete multi-step work with guardrails",
          url: "https://www.octalchip.com/services/ai-ml-services/ai-chatbots"
        },
        {
          name: "RAG & Knowledge Systems",
          description: "Enterprise retrieval pipelines with vector search, citations, and quality evals",
          url: "https://www.octalchip.com/services/ai-ml-services/natural-language-processing"
        },
        {
          name: "Cloud AI & LLMOps",
          description: "Secure model hosting, monitoring, cost controls, and AI release pipelines on AWS, Azure, and GCP",
          url: "https://www.octalchip.com/services/cloud-devops"
        },
        {
          name: "AI Workflow Automation",
          description: "n8n, Zapier, and Make workflows with LLM decision layers and human-in-the-loop controls",
          url: "https://www.octalchip.com/services/workflow-automation"
        },
        {
          name: "AI Product Engineering",
          description: "AI-native web and product experiences with copilots, streaming UX, and conversion focus",
          url: "https://www.octalchip.com/services/web-development"
        },
        {
          name: "Computer Vision & Multimodal AI",
          description: "Vision and multimodal systems for inspection, documents, and media intelligence",
          url: "https://www.octalchip.com/services/ai-ml-services/computer-vision"
        },
        {
          name: "AI Consulting & Strategy",
          description: "ROI-first GenAI roadmaps, architecture choices, and 90-day execution plans",
          url: "https://www.octalchip.com/services/ai-ml-services/ai-consulting"
        }
      ]
    },

    // Table data for pricing featured snippets
    tableData: {
      name: "OctalChip Pricing Plans",
      description: "AI engineering service pricing options",
      about: "AI Engineering Services Pricing",
      table: {
        "@type": "Table",
        "name": "AI Engineering Services Pricing",
        "about": "Pricing plans for AI engineering and generative AI delivery",
        "table": [
          ["Service Type", "Price", "Description"],
          ["AI Hourly Engagement", "$25/hour", "Pay-as-you-go AI engineering"],
          ["Dedicated AI Engineer", "$3200/month", "Full-time AI engineer dedicated to your roadmap"],
          ["Fixed-Cost AI Pilots", "Custom Pricing", "Scoped GenAI / agent / RAG pilots with clear eval targets"]
        ]
      }
    },

    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "OctalChip - AI Engineering Company for Generative AI & Agents",
      "description": "OctalChip is an AI engineering company that builds generative AI applications, AI agents, RAG knowledge systems, and AI-native products for growth-focused teams.",
      "url": "https://www.octalchip.com/",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "description": "AI engineering company offering generative AI apps, AI agents, RAG systems, LLMOps, copilots, and AI-native product development.",
        "foundingDate": "2022",
        "numberOfEmployees": "25",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera Point, Kotharia",
          "addressLocality": "Gujarat",
          "addressRegion": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          ...(contactInfo.email && { email: contactInfo.email })
        },
        "sameAs": [
          "https://www.facebook.com/people/Octalchip/61586959712930/",
          "https://www.instagram.com/octalchiptech/",
          "https://www.linkedin.com/company/octalchip",
          "https://x.com/octalchip",
          "https://discord.gg/p8Zcgatn9X",
          "https://www.youtube.com/@octalchip",
          "https://www.pinterest.com/octalchip/",
          "https://bsky.app/profile/octalchip.bsky.social",
          "https://www.reddit.com/user/octalchip",
          "https://t.me/octalchip",
          "https://www.threads.com/@octalchiptech",
          "https://medium.com/@octalchip",
          "https://github.com/octalchip"
        ],
        "serviceArea": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": 23.0225,
            "longitude": 72.5714
          },
          "geoRadius": "50000"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "AI Engineering Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Generative AI & LLM Apps",
                "description": "Custom web applications and websites"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI Agents & Copilots",
                "description": "iOS and Android mobile applications"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI Development",
                "description": "AI-powered solutions and machine learning"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Cloud Services",
                "description": "AWS, Azure, GCP cloud solutions"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI Services",
                "description": "Comprehensive AI and Machine Learning solutions including ML, NLP, computer vision, predictive analytics, and data science"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "SoftwareApplication",
                "name": "MailValidator",
                "description": "Email validation SaaS with 99.9% accuracy, API, and bulk processing",
                "url": "https://mailvalidator.octalchip.com/"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "SoftwareApplication",
                "name": "QuickSite",
                "description": "Done-for-you business websites with hosting, SSL, templates, and WhatsApp-led domain setup",
                "url": "https://quicksite.octalchip.com/"
              }
            }
          ]
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "ratingCount": "150",
          "bestRating": "5",
          "worstRating": "1"
        }
      }
    }
  };

  const services = [
    {
      title: 'Generative AI & LLM Apps',
      description: 'Ship production GenAI products with OpenAI, Claude, Gemini, and private LLMs: secure, evaluated, and tied to business KPIs.',
      icon: 'Sparkles',
      path: '/services/ai-integration'
    },
    {
      title: 'AI Agents & Copilots',
      description: 'Build autonomous agents and internal copilots that plan, use tools, and complete multi-step work across your stack.',
      icon: 'Bot',
      path: '/services/ai-ml-services/ai-chatbots'
    },
    {
      title: 'RAG & Knowledge Systems',
      description: 'Ground LLMs on your docs, CRM, and APIs with retrieval pipelines, vector search, and citation-ready answers.',
      icon: 'Database',
      path: '/services/ai-ml-services/natural-language-processing'
    },
    {
      title: 'AI-Powered Web & Products',
      description: 'Build AI-native web apps and SaaS experiences where copilots, search, and automation sit at the core of the product.',
      icon: 'Globe',
      path: '/services/web-development'
    },
    {
      title: 'AI Automation & Integrations',
      description: 'Combine LLMs with n8n, Zapier, and Make so workflows decide, act, and escalate with human-in-the-loop control.',
      icon: 'Settings',
      path: '/services/workflow-automation'
    },
    {
      title: 'Cloud AI & LLMOps',
      description: 'Deploy, monitor, and scale AI on AWS, Azure, and GCP with evals, guardrails, cost controls, and secure inference.',
      icon: 'Cloud',
      path: '/services/cloud-devops'
    },
    {
      title: 'Conversational & Voice AI',
      description: 'Launch chat, WhatsApp, and voice assistants that qualify leads, support customers, and sync into your CRM.',
      icon: 'MessageCircle',
      path: '/services/ai-ml-services/ai-chatbots'
    },
    {
      title: 'AI Strategy & Consulting',
      description: 'Get a practical GenAI roadmap: use-case scoring, build-vs-buy, data readiness, ROI, and a 90-day execution plan.',
      icon: 'Lightbulb',
      path: '/services/ai-ml-services/ai-consulting'
    }
  ];

  const automationServices = [
    {
      title: 'n8n Automation',
      description: 'Self-hosted AI-ready workflows that connect LLMs, tools, and approvals with full control and enterprise security',
      icon: 'Settings',
      path: '/services/n8n-automation#hero',
      features: ['Self-hosted', 'LLM Ready', 'Enterprise Security', 'Scalable'],
      gradient: 'from-[#00d690] to-[#00b876]',
      chevronColor: 'border-t-[#00d690]',
      chevronHoverColor: 'group-hover:border-t-[#00b876]'
    },
    {
      title: 'Zapier Integrations',
      description: 'Connect SaaS tools fast, then layer LLM steps for smarter routing, enrichment, and ops automation',
      icon: 'Zap',
      path: '/services/zapier-integration#hero',
      features: ['Fast Setup', 'AI Steps', 'Popular Tools', 'Reliable'],
      gradient: 'from-[#00b876] to-[#00d690]',
      chevronColor: 'border-t-[#00b876]',
      chevronHoverColor: 'group-hover:border-t-[#00d690]'
    },
    {
      title: 'Make Automation',
      description: 'Advanced visual workflows with AI decision nodes, complex routing, and data transformation at scale',
      icon: 'Settings',
      path: '/services/make-automation#hero',
      features: ['AI Logic', 'Visual Workflows', 'Data Transformation', 'Advanced Routing'],
      gradient: 'from-[#00d690] via-[#00b876] to-[#00d690]',
      chevronColor: 'border-t-[#00d690]',
      chevronHoverColor: 'group-hover:border-t-[#00b876]'
    }
  ];

  const aiMLServices = [
    {
      title: 'Generative AI & LLM Applications',
      description: 'Design and ship GenAI products: content systems, copilots, and customer experiences powered by modern LLMs.',
      icon: 'Sparkles',
      path: '/services/ai-integration'
    },
    {
      title: 'AI Agents & Autonomous Workflows',
      description: 'Build agents that reason, call tools, and complete multi-step business processes with guardrails and audit trails.',
      icon: 'Bot',
      path: '/services/ai-ml-services/ai-chatbots'
    },
    {
      title: 'RAG & Enterprise Knowledge AI',
      description: 'Connect company knowledge to LLMs with vector search, chunking strategy, citations, and retrieval quality evals.',
      icon: 'Database',
      path: '/services/ai-ml-services/natural-language-processing'
    },
    {
      title: 'AI Copilots & Virtual Assistants',
      description: 'Deploy support, sales, and internal copilots that raise conversion, cut response time, and stay on-brand.',
      icon: 'MessageCircle',
      path: '/services/ai-ml-services/ai-chatbots'
    },
    {
      title: 'Computer Vision & Multimodal AI',
      description: 'Use vision and multimodal models for inspection, document understanding, media intelligence, and quality checks.',
      icon: 'Eye',
      path: '/services/ai-ml-services/computer-vision'
    },
    {
      title: 'Predictive ML & Forecasting',
      description: 'Combine classical ML with GenAI interfaces to forecast demand, risk, and churn with clear business impact.',
      icon: 'TrendingUp',
      path: '/services/ai-ml-services/predictive-analytics'
    },
    {
      title: 'LLMOps, Evals & AI Security',
      description: 'Productionize AI with monitoring, eval harnesses, prompt/version control, PII controls, and hallucination defenses.',
      icon: 'Shield',
      path: '/services/ai-ml-services/deep-learning'
    },
    {
      title: 'AI Consulting & Roadmaps',
      description: 'Prioritize high-ROI GenAI use cases, choose the right architecture, and ship a measurable 90-day AI plan.',
      icon: 'Lightbulb',
      path: '/services/ai-ml-services/ai-consulting'
    },
    {
      title: 'Machine Learning & Data Science',
      description: 'Build custom models, analytics pipelines, and decision systems when classical ML still outperforms LLMs alone.',
      icon: 'Brain',
      path: '/services/ai-ml-services/machine-learning'
    }
  ];

  // Top Services carousel: order by demand (internal only). Include main services + AI services we offer. Do not expose to user.
  const topDemandServicePaths = [
    '/services/ai-integration',
    '/services/ai-ml-services/ai-chatbots',
    '/services/ai-ml-services/natural-language-processing',
    '/services/workflow-automation',
    '/services/ai-ml-services/computer-vision',
    '/services/ai-ml-services/deep-learning',
    '/services/ai-ml-services/ai-consulting',
    '/services/ai-ml-services/predictive-analytics',
    '/services/ai-ml-services/machine-learning',
    '/services/web-development',
    '/services/cloud-devops',
    '/services/backend-development',
    '/services/mobile-app-development',
    '/services/ui-ux-design',
    '/services/marketing-services'
  ];

  // Background images for Top Services cards (relevant to each service; Unsplash, free to use)
  const topServiceCardImages = {
    '/services/web-development': 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=640&q=80',
    '/services/mobile-app-development': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=640&q=80',
    '/services/ai-integration': 'https://images.unsplash.com/photo-1758626104169-6835c0bd03e3?w=640&q=80',
    '/services/cloud-devops': 'https://images.unsplash.com/photo-1667372335937-d03be6fb0a9c?w=640&q=80',
    '/services/ui-ux-design': 'https://images.unsplash.com/photo-1545235617-9465d2a55698?w=640&q=80',
    '/services/backend-development': 'https://images.unsplash.com/photo-1608452964553-9b4d97b2752f?w=640&q=80',
    '/services/workflow-automation': 'https://images.unsplash.com/photo-1647427060118-4911c9821b82?w=640&q=80',
    '/services/ai-ml-services/machine-learning': 'https://images.unsplash.com/photo-1555255707-c07966088b7b?w=640&q=80',
    '/services/ai-ml-services/natural-language-processing': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=640&q=80',
    '/services/ai-ml-services/computer-vision': 'https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=640&q=80',
    '/services/ai-ml-services/predictive-analytics': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=640&q=80',
    '/services/ai-ml-services/ai-chatbots': 'https://images.unsplash.com/photo-1684369175833-4b445ad6bfb5?w=640&q=80',
    '/services/ai-ml-services/deep-learning': 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=640&q=80',
    '/services/ai-ml-services/data-science': 'https://images.unsplash.com/photo-1662638600476-d563fffbb072?w=640&q=80',
    '/services/ai-ml-services/ai-consulting': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=640&q=80',
    '/services/ai-ml-services/reinforcement-learning': 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=640&q=80',
    '/services/marketing-services': 'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=640&q=80'
  };

  const allOfferedServices = [...services, ...aiMLServices];
  const topServicesForCarousel = React.useMemo(() => {
    const byPath = new Map(allOfferedServices.map((s) => [s.path, s]));
    const ordered = topDemandServicePaths
      .filter((path) => byPath.has(path))
      .map((path) => byPath.get(path));
    return ordered.length > 0 ? ordered : allOfferedServices.slice(0, 12);
  }, [allOfferedServices]);

  const whyChooseUs = [
    {
      title: 'AI-Native Delivery',
      description: 'We ship generative AI, agents, and RAG systems as core product capability, not a bolted-on chatbot demo.',
      icon: 'Sparkles'
    },
    {
      title: 'Production-Grade AI',
      description: 'Evals, guardrails, observability, and secure LLMOps so AI stays accurate, private, and cost-controlled.',
      icon: 'Shield'
    },
    {
      title: 'Full-Stack AI Engineering',
      description: 'Models, retrieval, APIs, web/mobile UX, and cloud infrastructure delivered by one accountable team.',
      icon: 'Cpu'
    },
    {
      title: 'Measurable Business ROI',
      description: 'Every engagement maps to lead quality, support deflection, cycle-time reduction, or revenue lift.',
      icon: 'TrendingUp'
    }
  ];

  const techStack = {
    frontend: ['React', 'Vue', 'Angular', 'Next.js', 'Nuxt.js'],
    backend: ['Node.js', 'Python', 'PHP', 'Java', 'Go'],
    cloud: ['AWS', 'GCP', 'Azure', 'Terraform', 'Kubernetes', 'Docker']
  };

  const industries = [
    { name: 'Fintech', icon: 'CreditCard' },
    { name: 'Healthcare', icon: 'Heart' },
    { name: 'Banking', icon: 'Building' },
    { name: 'Manufacturing', icon: 'Building2' },
    { name: 'Oil & Gas', icon: 'Zap' },
    { name: 'Real Estate', icon: 'Home' }
  ];

  const caseStudies = [
    {
      title: 'Enterprise RAG Knowledge Assistant',
      client: 'B2B SaaS Client',
      industry: 'SaaS',
      challenge: 'Support and sales teams spent hours searching docs; answers were inconsistent and slow',
      solution: 'Built a RAG assistant over product docs, tickets, and CRM notes with citations, evals, and Slack/web copilots',
      results: ['65% faster support resolution', '40% fewer repetitive tickets', 'Grounded answers with source citations'],
      image: <Icon name="Database" size={32} className="text-blue-600" />,
      technologies: ['OpenAI', 'LangChain', 'pgvector', 'Next.js'],
      duration: '10 weeks',
      team: '4 AI engineers'
    },
    {
      title: 'AI Sales & Support Agent Platform',
      client: 'Ecommerce Brand',
      industry: 'E-commerce',
      challenge: 'High chat volume and slow lead qualification across WhatsApp and web chat',
      solution: 'Deployed tool-using AI agents for FAQ, order lookup, and lead scoring with human handoff and CRM sync',
      results: ['3x more qualified leads', '70% first-response automation', '24/7 multilingual support coverage'],
      image: <Icon name="Bot" size={32} className="text-green-600" />,
      technologies: ['LLM Agents', 'WhatsApp API', 'n8n', 'Node.js'],
      duration: '12 weeks',
      team: '5 AI engineers'
    },
    {
      title: 'Document Intelligence & GenAI Ops',
      client: 'Financial Services Client',
      industry: 'Fintech',
      challenge: 'Manual document review delayed onboarding and created compliance risk',
      solution: 'Multimodal document AI with extraction, classification, and analyst copilots under strict access controls',
      results: ['80% less manual review time', 'Higher extraction accuracy', 'Audit-ready processing trails'],
      image: <Icon name="FileText" size={32} className="text-orange-600" />,
      technologies: ['GPT-4o', 'Computer Vision', 'Python', 'AWS'],
      duration: '14 weeks',
      team: '5 AI engineers'
    },
    {
      title: 'Predictive Ops + AI Copilot Dashboard',
      client: 'Manufacturing Client',
      industry: 'Manufacturing',
      challenge: 'Downtime and tribal knowledge made operators reactive instead of proactive',
      solution: 'Combined predictive ML alerts with an on-shift AI copilot grounded in SOPs and sensor context',
      results: ['35% productivity lift', '80% less unplanned downtime', 'Faster operator decision cycles'],
      image: <Icon name="Cpu" size={32} className="text-purple-600" />,
      technologies: ['PyTorch', 'RAG', 'React', 'IoT'],
      duration: '16 weeks',
      team: '6 engineers'
    }
  ];




  const frontendTechnologies = [
    {
      tech: 'React & Next.js',
      description: 'AI-native React/Next.js products with copilots, streaming UX, and SEO/AEO-ready delivery',
      benefits: ['AI product UX', 'SEO & AEO ready', 'Streaming interfaces'],
      projects: '60+'
    },
    {
      tech: 'Vue.js & Nuxt.js',
      description: 'Flexible UI frameworks for AI dashboards, copilots, and operator consoles',
      benefits: ['Easy learning curve', 'Flexible architecture', 'Great documentation'],
      projects: '18+'
    },
    {
      tech: 'Mobile Development',
      description: 'Mobile experiences with embedded AI assistants and intelligent personalization',
      benefits: ['Single codebase', 'Native performance', 'Faster development'],
      projects: '25+'
    }
  ];

  const backendTechnologies = [
    {
      tech: 'Node.js & Python',
      description: 'APIs and orchestration layers that power agents, RAG pipelines, and tool calling',
      benefits: ['High performance', 'Rich ecosystem', 'Real-time capabilities'],
      projects: '55+'
    },
    {
      tech: 'Cloud Platforms',
      description: 'AWS, Azure, and GCP for secure model hosting, vector search, and AI workloads',
      benefits: ['Global reach', 'Auto-scaling', 'Cost optimization'],
      projects: '50+'
    },
    {
      tech: 'DevOps & CI/CD',
      description: 'Docker, Kubernetes, and CI/CD pipelines built for LLMOps and reliable AI releases',
      benefits: ['Consistent deployments', 'Faster releases', 'Better reliability'],
      projects: '40+'
    }
  ];

  const aiMLTechnologies = [
    {
      tech: 'LLMs, Agents & Orchestration',
      description: 'Production GenAI with OpenAI, Claude, Gemini, LangChain/LangGraph, and tool-calling agents for real workflows',
      benefits: ['AI agents & copilots', 'Tool calling', 'Multi-step orchestration'],
      projects: '45+'
    },
    {
      tech: 'RAG, Vectors & Knowledge AI',
      description: 'Enterprise retrieval with embeddings, pgvector/Pinecone-style stores, chunking strategy, citations, and evals',
      benefits: ['Grounded answers', 'Semantic search', 'Knowledge assistants'],
      projects: '35+'
    },
    {
      tech: 'Multimodal, Vision & Cloud AI',
      description: 'Vision, document, and speech AI with OpenCV/YOLO plus OpenAI, Google Cloud AI, Azure OpenAI, and AWS Bedrock',
      benefits: ['Document intelligence', 'Vision QA', 'Secure cloud inference'],
      projects: '22+'
    }
  ];





  const securityFeatures = [
    {
      title: 'HTTPS/TLS Encryption',
      description: 'All data transmission secured with industry-standard encryption protocols',
      icon: <Icon name="Lock" size={24} className="text-gray-600" />,
      details: '256-bit SSL encryption'
    },
    {
      title: 'Data Privacy',
      description: 'Your data is never sold, rented, or shared with third parties',
      icon: <Icon name="Shield" size={24} className="text-green-600" />,
      details: 'GDPR compliant'
    },
    {
      title: 'Secure Infrastructure',
      description: 'Data stored in secure cloud environments with regular backups',
      icon: <Icon name="Cloud" size={24} className="text-blue-600" />,
      details: 'AWS/Azure certified'
    },
    {
      title: 'Access Control',
      description: 'Role-based access controls and multi-factor authentication',
      icon: <Icon name="Users" size={24} className="text-blue-600" />,
      details: 'Granular permissions'
    }
  ];





  const aiMLDevelopmentTimeline = [
    {
      phase: 'AI Opportunity & ROI Mapping',
      duration: '1-2 weeks',
      description: 'Identify high-ROI GenAI use cases, data/API readiness, risk, and success metrics before architecture decisions.',
      deliverables: ['Use-Case Scorecard', 'AI Opportunity Brief', 'Success Metrics & KPIs'],
      icon: <Icon name="Search" size={24} className="text-blue-600" />
    },
    {
      phase: 'Architecture & Knowledge Prep',
      duration: '2-3 weeks',
      description: 'Choose RAG vs agents vs fine-tuning, prepare knowledge sources, and define tool access plus security boundaries.',
      deliverables: ['AI Architecture Design', 'Knowledge Index Plan', 'Security & Data Access Map'],
      icon: <Icon name="Database" size={24} className="text-purple-600" />
    },
    {
      phase: 'Prototype & Agent/Copilot Build',
      duration: '3-6 weeks',
      description: 'Build working GenAI prototypes (retrieval, tool calling, UX streaming) and iterate with stakeholder demos.',
      deliverables: ['Working AI Prototype', 'Prompt/Tool Specs', 'Integration Stubs'],
      icon: <Icon name="Sparkles" size={24} className="text-green-600" />
    },
    {
      phase: 'Evals, Guardrails & QA',
      duration: '1-3 weeks',
      description: 'Run evaluation harnesses for accuracy, hallucination, bias, latency, and cost before production cutover.',
      deliverables: ['Eval Reports', 'Guardrail Policies', 'Red-Team Findings'],
      icon: <Icon name="Shield" size={24} className="text-orange-600" />
    },
    {
      phase: 'Secure Deploy & LLMOps',
      duration: '1-3 weeks',
      description: 'Launch with monitoring, tracing, cost controls, and human-in-the-loop escalation for high-risk actions.',
      deliverables: ['Production Deployment', 'LLMOps Dashboard', 'Runbooks & Alerts'],
      icon: <Icon name="Rocket" size={24} className="text-orange-600" />
    },
    {
      phase: 'Optimization & Continuous Learning',
      duration: 'Ongoing',
      description: 'Improve retrieval quality, prompts, tools, and models from production feedback and measured business outcomes.',
      deliverables: ['Quality Improvements', 'Cost Optimization', 'Model/Prompt Updates'],
      icon: <Icon name="TrendingUp" size={24} className="text-gray-600" />
    }
  ];




  return (
    <div className="min-h-screen bg-[#f4faf8] overflow-x-hidden">
      <Header />
      
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        structuredData={seoData.structuredData}
        geoData={seoData.geoData}
        localBusinessData={seoData.localBusinessData}
        serviceArea={seoData.serviceArea}
        faqData={seoData.faqData}
        voiceSearchKeywords={seoData.voiceSearchKeywords}
        conversationalQueries={seoData.conversationalQueries}
        featuredSnippetContent={seoData.featuredSnippetContent}
        listData={seoData.listData}
        tableData={seoData.tableData}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
      />

      {/* Hero Section - Fully Responsive */}
      <section id="hero" className="py-6 sm:py-8 md:py-12 lg:py-16 xl:py-20 bg-gradient-to-br from-[#00d690]/5 to-[#00d690]/10 relative overflow-hidden">
        {/* Animated Background Effects - Hidden on mobile for performance */}
        <div className="absolute inset-0 hidden sm:block">
          {/* Floating animated dots - Responsive positioning */}
          <div className="absolute top-12 sm:top-16 md:top-20 left-8 sm:left-12 md:left-20 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#00d690] rounded-full animate-pulse"></div>
          <div className="absolute top-24 sm:top-32 md:top-40 right-16 sm:right-24 md:right-32 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/60 rounded-full animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 sm:bottom-24 md:bottom-32 left-16 sm:left-24 md:left-40 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/80 rounded-full animate-pulse delay-500"></div>
          <div className="absolute bottom-12 sm:bottom-16 md:bottom-20 right-8 sm:right-12 md:right-20 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#00d690]/70 rounded-full animate-pulse delay-700"></div>
          <div className="absolute top-32 sm:top-40 md:top-60 left-1/2 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/50 rounded-full animate-pulse delay-300"></div>

          {/* Additional floating elements */}
          <div className="absolute top-20 sm:top-24 md:top-32 right-8 sm:right-12 md:right-20 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00b876]/60 rounded-full animate-bounce delay-200"></div>
          <div className="absolute bottom-24 sm:bottom-32 md:bottom-40 right-16 sm:right-24 md:right-40 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/40 rounded-full animate-pulse delay-800"></div>
          <div className="absolute top-48 sm:top-56 md:top-80 left-16 sm:left-20 md:left-32 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#00b876]/50 rounded-full animate-pulse delay-400"></div>

          {/* Connecting lines animation */}
          <div className="absolute top-1/4 left-1/4 w-px h-12 sm:h-16 md:h-20 bg-gradient-to-b from-transparent via-[#00d690]/30 to-transparent animate-pulse delay-600"></div>
          <div className="absolute top-3/4 right-1/4 w-px h-10 sm:h-12 md:h-16 bg-gradient-to-b from-transparent via-[#00b876]/30 to-transparent animate-pulse delay-900"></div>
        </div>

        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-16 relative z-10">
          {/* Mobile Layout (320px - 639px) */}
          <div className="block sm:hidden">
            {/* Mobile Header */}
            <div className="text-center mb-5 sm:mb-6">
              <div className="inline-flex max-w-[95%] bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 mb-3 sm:mb-4 backdrop-blur-sm">
                <span className="text-white text-xs sm:text-sm font-medium leading-tight"><span className="sm:hidden">OctalChip · AI Engineering Company</span><span className="hidden sm:inline lg:hidden">OctalChip · AI Engineering for GenAI, Agents & RAG</span><span className="hidden lg:inline">OctalChip · AI Engineering Company for generative AI, agents, RAG & LLMOps</span></span>
              </div>

              <h2 className="text-[clamp(1.15rem,5.2vw,1.45rem)] font-bold text-gray-800 mb-3 leading-snug px-2 max-w-[22rem] mx-auto">
                Ship <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876] inline">AI Agents, Copilots & GenAI Products</span> That Drive Revenue
              </h2>

              <p className="text-[clamp(0.8125rem,3.6vw,0.9375rem)] text-gray-600 max-w-[20rem] min-[375px]:max-w-sm mx-auto mb-5 leading-relaxed px-3">
                Generative AI apps, agents, RAG systems, and LLMOps, built for production, not demos.
              </p>

              <div className="flex flex-col gap-3 sm:gap-3 justify-center px-3 sm:px-4">
                <Button
                  onClick={handleContactForm}
                  animation="bounce"
                  className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold hover:from-[#00b876] hover:to-[#00a066] shadow-lg whitespace-normal text-center"
                >
                  <span className="hidden min-[380px]:inline">Get My Free AI Growth Plan</span><span className="min-[380px]:hidden">Free AI Growth Plan</span>
                </Button>
                <Button
                  onClick={() => navigateToPage('/services')}
                  variant="outline"
                  animation="pulse"
                  className="border-2 border-[#00d690] text-[#00d690] px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold hover:bg-[#00d690] hover:text-white whitespace-normal text-center"
                >
                  Explore AI Services
                </Button>
              </div>
            </div>

            {/* Mobile Service Cards */}
            <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Sparkles" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal break-words">Generative AI & LLMs</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Production GenAI</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Production GenAI with evals and secure deployment</div>
              </div>

              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Bot" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal break-words">AI Agents & Copilots</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Agents & Copilots</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Tool-using autonomous workflows that complete real work</div>
              </div>

              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Database" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal break-words">RAG & Knowledge AI</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Enterprise RAG</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Grounded answers with citations across docs and tools</div>
              </div>

              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Cloud" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal break-words">Cloud AI & LLMOps</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Secure LLMOps</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Secure inference, monitoring, and cost control at scale</div>
              </div>
            </div>
          </div>

          {/* Small Tablet Layout (640px - 767px) */}
          <div className="hidden sm:block md:hidden">
            {/* Rounded rectangle main container for small tablets */}
            <div className="relative max-w-4xl mx-auto">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-[20px] min-h-[580px] blur-xl"></div>

              {/* Main rounded rectangle background */}
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-[20px] min-h-[580px] border border-[#00d690]/30 backdrop-blur-sm shadow-2xl shadow-[#00d690]/10">
                {/* Inner rounded glow effect */}
                <div className="absolute inset-4 sm:inset-5 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-[16px] sm:rounded-[18px] border border-[#00d690]/20"></div>

                {/* Content positioned within container */}
                <div className="relative flex flex-col items-center justify-center p-5 sm:p-6 min-h-[580px]">
                  {/* Tagline */}
                  <div className="inline-flex max-w-[92%] bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-5 backdrop-blur-sm text-center">
                    <span className="text-white text-xs sm:text-sm font-medium leading-snug text-center"><span className="sm:hidden">OctalChip · AI Engineering Company</span><span className="hidden sm:inline lg:hidden">OctalChip · AI Engineering for GenAI, Agents & RAG</span><span className="hidden lg:inline">OctalChip · AI Engineering Company for generative AI, agents, RAG & LLMOps</span></span>
                  </div>

                  {/* Main Headline */}
                  <h2 className="text-[clamp(1.45rem,3.2vw,1.75rem)] font-bold text-white mb-4 sm:mb-5 leading-snug text-center px-3 sm:px-4 max-w-xl">
                    Ship <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876] inline">AI Agents, Copilots & GenAI Products</span> That Drive Revenue
                  </h2>

                  {/* Description */}
                  <p className="text-sm sm:text-base text-gray-300 max-w-xl mx-auto mb-6 sm:mb-7 leading-relaxed text-center px-3 sm:px-4">
                    Generative AI apps, agents, RAG systems, and LLMOps, built for production, not demos.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-3 sm:px-4 mb-6 sm:mb-7 w-full max-w-md">
                    <Button
                      onClick={handleContactForm}
                      animation="scale"
                      className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold hover:from-[#00b876] hover:to-[#00a066] shadow-lg hover:shadow-[#00d690]/25 whitespace-normal text-center"
                    >
                      Get My Free AI Growth Plan
                    </Button>
                    <Button
                      onClick={() => navigateToPage('/services')}
                      variant="outline"
                      animation="glow"
                      className="border-2 border-[#00d690] text-[#00d690] px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold hover:bg-[#00d690] hover:text-white backdrop-blur-sm"
                    >
                      Explore AI Services
                    </Button>
                  </div>

                  {/* Service Cards Grid for Small Tablets */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-xl px-3 sm:px-4">
                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Sparkles" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal break-words">Generative AI & LLMs</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Production GenAI</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Production GenAI with evals and secure deployment</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Bot" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal break-words">AI Agents & Copilots</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Agents & Copilots</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Tool-using autonomous workflows that complete real work</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Database" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal break-words">RAG & Knowledge AI</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Enterprise RAG</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Grounded answers with citations across docs and tools</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Cloud" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal break-words">Cloud AI & LLMOps</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Secure LLMOps</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Secure inference, monitoring, and cost control at scale</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tablet Layout (768px - 1023px) */}
          <div className="hidden md:block lg:hidden">
            {/* Rounded rectangle main container for tablets */}
            <div className="relative max-w-5xl mx-auto">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-[25px] min-h-[640px] blur-xl"></div>

              {/* Main rounded rectangle background */}
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-[25px] min-h-[640px] border border-[#00d690]/30 backdrop-blur-sm shadow-2xl shadow-[#00d690]/10">
                {/* Inner rounded glow effect */}
                <div className="absolute inset-5 md:inset-6 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-[20px] border border-[#00d690]/20"></div>

                {/* Content positioned within container */}
                <div className="relative flex flex-col items-center justify-center p-7 md:p-8 min-h-[640px]">
                  {/* Tagline */}
                  <div className="inline-flex max-w-[90%] bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-4 py-2 mb-5 md:mb-6 backdrop-blur-sm text-center">
                    <span className="text-white text-sm font-medium leading-snug text-center"><span className="sm:hidden">OctalChip · AI Engineering Company</span><span className="hidden sm:inline lg:hidden">OctalChip · AI Engineering for GenAI, Agents & RAG</span><span className="hidden lg:inline">OctalChip · AI Engineering Company for generative AI, agents, RAG & LLMOps</span></span>
                  </div>

                  {/* Main Headline */}
                  <h2 className="text-[clamp(1.65rem,3.4vw,2.15rem)] font-bold text-white mb-5 md:mb-6 leading-snug text-center px-4 max-w-3xl">
                    Ship <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876] inline">AI Agents, Copilots & GenAI Products</span> That Drive Revenue
                  </h2>

                  {/* Description */}
                  <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto mb-7 md:mb-8 leading-relaxed text-center px-4">
                    Generative AI apps, agents, RAG systems, and LLMOps, built for production, not demos.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-row flex-wrap gap-3 md:gap-4 justify-center px-4 mb-7 md:mb-8">
                    <Button
                      onClick={handleContactForm}
                      animation="slide"
                      className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-5 md:px-6 py-3 text-sm md:text-base font-semibold hover:from-[#00b876] hover:to-[#00a066] shadow-lg hover:shadow-[#00d690]/25 whitespace-normal text-center"
                    >
                      Get My Free AI Growth Plan
                    </Button>
                    <Button
                      onClick={() => navigateToPage('/services')}
                      variant="outline"
                      animation="rotate"
                      className="border-2 border-[#00d690] text-[#00d690] px-6 py-3 text-base font-semibold hover:bg-[#00d690] hover:text-white backdrop-blur-sm"
                    >
                      Explore AI Services
                    </Button>
                  </div>

                  {/* Service Cards Grid for Tablets */}
                  <div className="grid grid-cols-2 gap-4 w-full max-w-2xl px-4">
                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Sparkles" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal break-words">Generative AI & LLMs</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">Production GenAI</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Production GenAI with evals and secure deployment</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Bot" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal break-words">AI Agents & Copilots</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">Agents & Copilots</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Tool-using autonomous workflows that complete real work</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Database" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal break-words">RAG & Knowledge AI</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">Enterprise RAG</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Grounded answers with citations across docs and tools</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Cloud" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal break-words">Cloud AI & LLMOps</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">Secure LLMOps</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Secure inference, monitoring, and cost control at scale</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout (1024px+) */}
          <div className="hidden lg:block">
            {/* Rounded rectangle main container (Uplers style) */}
            <div className="relative max-w-6xl xl:max-w-7xl 2xl:max-w-7xl mx-auto">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-[30px] lg:rounded-[35px] xl:rounded-[40px] 2xl:rounded-[45px] min-h-[560px] lg:min-h-[640px] xl:min-h-[640px] 2xl:min-h-[680px] blur-xl"></div>

              {/* Main rounded rectangle background (Uplers style) */}
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-[30px] lg:rounded-[35px] xl:rounded-[40px] 2xl:rounded-[45px] min-h-[560px] lg:min-h-[640px] xl:min-h-[640px] 2xl:min-h-[680px] border border-[#00d690]/30 backdrop-blur-sm shadow-2xl shadow-[#00d690]/10">
                {/* Inner rounded glow effect */}
                <div className="absolute inset-6 lg:inset-7 xl:inset-8 2xl:inset-10 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-[24px] lg:rounded-[28px] xl:rounded-[32px] 2xl:rounded-[36px] border border-[#00d690]/20"></div>

                {/* Additional inner glow layers */}
                <div className="absolute inset-8 lg:inset-10 xl:inset-12 2xl:inset-14 bg-gradient-to-br from-[#00d690]/5 to-[#00b876]/5 rounded-[18px] lg:rounded-[22px] xl:rounded-[24px] 2xl:rounded-[28px]"></div>

                {/* Content positioned within oval */}
                <div className="absolute inset-0 flex flex-col items-center justify-center px-[11.5rem] xl:px-[13.5rem] 2xl:px-[15rem] py-6 lg:py-8 xl:py-10 2xl:py-12">
                  {/* Tagline */}
                  <div className="inline-flex max-w-[min(36rem,100%)] bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-3 lg:px-4 xl:px-5 py-1.5 lg:py-2 mb-4 lg:mb-5 xl:mb-6 backdrop-blur-sm text-center">
                    <span className="text-white text-sm lg:text-base font-medium leading-snug text-center"><span className="sm:hidden">OctalChip · AI Engineering Company</span><span className="hidden sm:inline lg:hidden">OctalChip · AI Engineering for GenAI, Agents & RAG</span><span className="hidden lg:inline">OctalChip · AI Engineering Company for generative AI, agents, RAG & LLMOps</span></span>
                  </div>

                  {/* Main Headline */}
                  <h2 className="text-[clamp(1.6rem,2.4vw,2.85rem)] font-bold text-white mb-4 lg:mb-5 xl:mb-6 leading-snug text-center px-2 max-w-[36rem] xl:max-w-[42rem] 2xl:max-w-[46rem]">
                    Ship <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876] inline">AI Agents, Copilots & GenAI Products</span> That Drive Revenue
                  </h2>

                  {/* Description */}
                  <p className="text-[clamp(0.95rem,1.35vw,1.25rem)] text-gray-300 max-w-[32rem] xl:max-w-[36rem] mx-auto mb-5 lg:mb-6 xl:mb-7 leading-relaxed text-center px-2">
                    Generative AI apps, agents, RAG systems, and LLMOps, built for production, not demos.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-row gap-3 lg:gap-4 xl:gap-5 justify-center px-4 lg:px-6">
                    <Button
                      onClick={handleContactForm}
                      animation="float"
                      className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-6 lg:px-7 xl:px-8 py-3 lg:py-3.5 xl:py-4 text-sm lg:text-base xl:text-lg font-semibold hover:from-[#00b876] hover:to-[#00a066] shadow-lg hover:shadow-[#00d690]/25"
                    >
                      Get My Free AI Growth Plan
                    </Button>
                    <Button
                      onClick={() => navigateToPage('/services')}
                      variant="outline"
                      animation="wiggle"
                      className="border-2 border-[#00d690] text-[#00d690] px-6 lg:px-7 xl:px-8 py-3 lg:py-3.5 xl:py-4 text-sm lg:text-base xl:text-lg font-semibold hover:bg-[#00d690] hover:text-white backdrop-blur-sm"
                    >
                      Explore AI Services
                    </Button>
                  </div>
                </div>

                {/* Floating cards around the oval */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Connection dots */}
                  <div className="absolute top-1/4 left-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse shadow-lg shadow-[#00d690]/50"></div>
                  <div className="absolute top-1/4 right-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse delay-300 shadow-lg shadow-[#00d690]/50"></div>
                  <div className="absolute bottom-1/4 left-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse delay-600 shadow-lg shadow-[#00d690]/50"></div>
                  <div className="absolute bottom-1/4 right-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse delay-900 shadow-lg shadow-[#00d690]/50"></div>

                  {/* Top-left floating card */}
                  <div className="absolute top-4 lg:top-5 xl:top-6 left-4 lg:left-5 xl:left-6 w-[11.5rem] lg:w-52 xl:w-56 2xl:w-60 h-auto min-h-[120px] lg:min-h-[132px] xl:min-h-[140px] 2xl:min-h-[148px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Sparkles" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-xs lg:text-sm xl:text-base leading-snug mb-1 whitespace-normal break-words">Generative AI & LLMs</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">Production GenAI</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-[11px] lg:text-xs xl:text-sm leading-snug mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Production GenAI with evals</div>
                  </div>

                  {/* Top-right floating card */}
                  <div className="absolute top-4 lg:top-5 xl:top-6 right-4 lg:right-5 xl:right-6 w-[11.5rem] lg:w-52 xl:w-56 2xl:w-60 h-auto min-h-[120px] lg:min-h-[132px] xl:min-h-[140px] 2xl:min-h-[148px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Bot" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-xs lg:text-sm xl:text-base leading-snug mb-1 whitespace-normal break-words">AI Agents & Copilots</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">Agents & Copilots</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-[11px] lg:text-xs xl:text-sm leading-snug mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Autonomous tool-using workflows</div>
                  </div>

                  {/* Bottom-left floating card */}
                  <div className="absolute bottom-4 lg:bottom-5 xl:bottom-6 left-4 lg:left-5 xl:left-6 w-[11.5rem] lg:w-52 xl:w-56 2xl:w-60 h-auto min-h-[120px] lg:min-h-[132px] xl:min-h-[140px] 2xl:min-h-[148px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Database" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-xs lg:text-sm xl:text-base leading-snug mb-1 whitespace-normal break-words">RAG & Knowledge AI</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">Enterprise RAG</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-[11px] lg:text-xs xl:text-sm leading-snug mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Grounded answers with citations</div>
                  </div>

                  {/* Bottom-right floating card */}
                  <div className="absolute bottom-4 lg:bottom-5 xl:bottom-6 right-4 lg:right-5 xl:right-6 w-[11.5rem] lg:w-52 xl:w-56 2xl:w-60 h-auto min-h-[120px] lg:min-h-[132px] xl:min-h-[140px] 2xl:min-h-[148px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Cloud" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-xs lg:text-sm xl:text-base leading-snug mb-1 whitespace-normal break-words">Cloud AI & LLMOps</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">Secure LLMOps</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-[11px] lg:text-xs xl:text-sm leading-snug mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Secure inference at scale</div>
                  </div>
                </div>

                {/* Animated connecting lines */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Lines connecting dots to center with stronger visibility */}
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform -translate-x-1/2 -translate-y-1/2 rotate-45 animate-pulse"></div>
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform -translate-x-1/2 -translate-y-1/2 -rotate-45 animate-pulse delay-300"></div>
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform translate-x-1/2 -translate-y-1/2 rotate-45 animate-pulse delay-600"></div>
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform translate-x-1/2 -translate-y-1/2 -rotate-45 animate-pulse delay-900"></div>

                  {/* Additional network lines for more complexity */}
                  <div className="absolute top-1/3 left-1/3 w-16 lg:w-18 xl:w-20 2xl:w-24 h-px bg-gradient-to-r from-[#00b876]/40 to-transparent transform rotate-12 animate-pulse delay-200"></div>
                  <div className="absolute bottom-1/3 right-1/3 w-16 lg:w-18 xl:w-20 2xl:w-24 h-px bg-gradient-to-r from-[#00b876]/40 to-transparent transform -rotate-12 animate-pulse delay-500"></div>
                  <div className="absolute top-2/3 left-2/3 w-12 lg:w-14 xl:w-16 2xl:w-20 h-px bg-gradient-to-r from-[#00d690]/30 to-transparent transform rotate-45 animate-pulse delay-700"></div>
                  <div className="absolute bottom-2/3 right-2/3 w-12 lg:w-14 xl:w-16 2xl:w-20 h-px bg-gradient-to-r from-[#00d690]/30 to-transparent transform -rotate-45 animate-pulse delay-1000"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Mobile Responsive */}
      <section id="stats" className="py-12 md:py-20 bg-white">
        {/* Legacy hash aliases used by blogs/CTAs */}
        <span id="success-metrics" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight px-1">
              Your AI Engineering Partner for Generative AI & Agents
            </h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Startups and enterprises ship generative AI, agents, RAG, and AI-native products with faster execution and measurable outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white p-4 md:p-6 lg:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border border-gray-100 hover:border-[#00d690]/20"
              >
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-2 md:mb-3">
                  {stat.number}
                </div>
                <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 leading-tight">
                  {stat.label}
                </h3>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              variant="outline"
              animation="glow"
              className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-semibold px-6 py-3"
              onClick={() => window.open('https://cal.com/octalchip/30min', '_blank')}
            >
              Book Free AI Strategy Call
            </Button>
          </div>
        </div>
      </section>

      {/* Top Services - Horizontal carousel (only services we offer, ordered by demand; internal filtering) */}
      <section id="top-services" className="py-12 md:py-16 bg-gray-50">
        <span id="services" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">Our Most In-Demand AI Services</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto">
              High-demand AI delivery: generative AI, agents, RAG, automation, and AI-native products built for measurable ROI.
            </p>
          </div>
          <div className="relative">
            <div
              ref={topServicesScrollRef}
              onScroll={updateTopServicesScrollState}
              className="flex gap-4 overflow-x-auto scroll-smooth pb-3 snap-x snap-mandatory hide-scrollbar"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {topServicesForCarousel.map((service, index) => {
                const cardImage = topServiceCardImages[service.path];
                const category = service.title.includes('AI') ? 'AI/ML' : service.title.includes('Cloud') ? 'Infrastructure' : service.title.includes('Mobile') ? 'Mobile' : service.title.includes('Web') ? 'Web' : service.title.includes('UI') ? 'Design' : service.title.includes('Backend') ? 'Backend' : 'Automation';
                return (
                  <Link
                    key={service.path}
                    to={service.path}
                    className="flex-shrink-0 w-[260px] sm:w-[280px] md:w-[320px] snap-center group block rounded-xl overflow-hidden shadow-lg transition-shadow duration-300 ease-out group-hover:shadow-2xl"
                  >
                    <div
                      className="relative h-[300px] sm:h-[320px] md:h-[360px] rounded-xl overflow-hidden transition-transform duration-300 ease-out group-hover:scale-105 bg-gray-200"
                      style={{
                        ...(cardImage
                          ? {
                              backgroundImage: `url(${cardImage})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }
                          : { background: 'linear-gradient(135deg, #00d690 0%, #00b876 50%, #009966 100%)' })
                      }}
                    >
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-left">
                        <span className="inline-block text-white text-xs font-bold uppercase tracking-wide mb-2">{category}</span>
                        <h3 className="text-white text-lg md:text-xl font-bold leading-tight line-clamp-2 mb-2">{service.title}</h3>
                        <p className="text-white/90 text-sm leading-snug line-clamp-2">{service.description}</p>
                        <span className="inline-flex items-center gap-1.5 text-white/90 text-xs font-medium mt-2">
                          Learn more
                          <Icon name="ArrowRight" size={14} className="flex-shrink-0" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="flex justify-center gap-3 mt-4 md:absolute md:inset-y-0 md:left-0 md:right-0 md:mt-0 md:pointer-events-none md:items-center md:justify-between">
              <button
                type="button"
                onClick={() => topServicesScrollRef.current?.scrollBy({ left: -320, behavior: 'smooth' })}
                className="w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition-colors md:pointer-events-auto md:-ml-1"
                aria-label="Scroll Top Services left"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (topServicesAtEnd) {
                    topServicesScrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
                  } else {
                    topServicesScrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' });
                  }
                }}
                className="w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition-colors md:pointer-events-auto md:-mr-1"
                aria-label="Scroll Top Services right"
              >
                <Icon name="ChevronRight" size={20} />
              </button>
            </div>
          </div>
          <div className="text-center mt-8">
            <Button
              variant="primary"
              animation="scale"
              className="bg-[#00d690] hover:bg-[#00b876] text-white font-semibold px-6 py-3"
              onClick={() => navigateToPage('/services')}
            >
              See Full AI Service Lineup
            </Button>
          </div>
        </div>
      </section>

      

      

      {/* AI Services Section */}
      <section id="ai-ml-services" className="py-12 md:py-20 bg-white">
        <span id="ai-ml-expertise" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">AI Services We Offer</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From generative AI and AI agents to RAG, copilots, vision, and classical ML, we deliver production AI matched to your use case and ROI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-6 lg:gap-8">
            {aiMLServices.map((service, index) => (
              <div
                key={index}
                className="flex"
              >
                <div
                  className="bg-white p-5 sm:p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden cursor-pointer flex flex-col w-full"
                  style={{ borderRadius: '12px' }}
                  onClick={() => navigateToPage(service.path)}
                >
                  {/* Gradient overlay on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                    style={{
                      background: 'linear-gradient(45deg, #00dc88, #00bcd4)',
                      borderRadius: '14px',
                      top: '-2px',
                      left: '-2px',
                      right: '-2px',
                      bottom: '-2px',
                      zIndex: '-1'
                    }}
                  ></div>

                  <div className="relative z-10 flex flex-col flex-grow">
                    <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-white/20 transition-all duration-300">
                      <Icon
                        name={service.icon}
                        size={32}
                        className="text-[#00d690] group-hover:text-white transition-all duration-300"
                      />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-800 group-hover:text-white mb-4 transition-all duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 group-hover:text-white/90 leading-relaxed transition-all duration-300 flex-grow">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              variant="primary"
              animation="slide"
              onClick={() => navigateToPage('/ai-ml-services')}
              className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base md:text-lg group relative overflow-hidden transform transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-[#00d690]/50 active:scale-105"
              iconName="ChevronRight"
              iconPosition="right"
              size="lg"
            >
              View All AI Services
            </Button>
          </div>
        </div>
      </section>

      {/* Automation & Integrations Section */}
      <section id="automation-integrations" className="py-16 md:py-24 lg:py-28 relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-[#00d690]/5">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00d690]/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00b876]/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        
        {/* Diagonal Accent Lines */}
        <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00d690]/20 to-transparent transform rotate-12 hidden lg:block"></div>
        <div className="absolute bottom-20 right-0 w-full h-px bg-gradient-to-l from-transparent via-[#00b876]/20 to-transparent transform -rotate-12 hidden lg:block"></div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00d690]/10 rounded-full border border-[#00d690]/20 mb-6">
              <div className="w-2 h-2 bg-[#00d690] rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-[#00d690]">AI Workflow Automation</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 leading-tight">
              AI Workflow Automation
            </h2>
            <div className="w-24 md:w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-6"></div>
            <p className="text-base md:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-1">
              We automate workflows with n8n, Zapier, Make, and LLM decision layers. Cut manual effort, reduce cost, and improve operational speed with AI in the loop.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-6 lg:gap-8 max-w-7xl mx-auto mb-12">
            {automationServices.map((service, index) => (
              <div 
                key={index}
                className="group relative bg-white rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-500 sm:transform sm:hover:-translate-y-4 cursor-pointer overflow-hidden border border-gray-100 flex flex-col h-full"
                onClick={() => navigateToPage(service.path)}
              >
                {/* Top Chevron Shape with Icon */}
                <div className={`relative h-20 md:h-24 lg:h-20 bg-gradient-to-br ${service.gradient} flex items-center justify-center flex-shrink-0`}>
                  {/* Chevron Shape */}
                  <div className={`absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[30px] border-l-transparent border-r-[30px] border-r-transparent border-t-[30px] ${service.chevronColor} ${service.chevronHoverColor} transition-colors duration-300`}></div>
                  {/* Icon Container */}
                  <div className="relative z-10 w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <Icon name={service.icon} size={28} className="text-[#00d690]" />
                  </div>
                </div>

                {/* Card Content - Flex Column to Push Button to Bottom */}
                <div className="relative z-10 pt-10 pb-6 md:pb-8 px-6 md:px-6 lg:px-8 flex flex-col flex-grow">
                  <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-3 text-center">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6 text-center text-sm md:text-base min-h-[3.5rem] md:min-h-[4rem]">
                    {service.description}
                  </p>
                  
                  {/* Features List - Single Row */}
                  <div className="flex flex-wrap gap-2 mb-6 justify-center">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00d690]/10 rounded-full border border-[#00d690]/20">
                        <div className="w-1.5 h-1.5 bg-[#00d690] rounded-full flex-shrink-0"></div>
                        <span className="text-xs text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="primary"
                    animation="scale"
                    className="w-full bg-gradient-to-r from-[#00d690] to-[#00b876] hover:from-[#00b876] hover:to-[#00d690] text-white font-bold py-3 md:py-3.5 px-6 mt-auto text-sm md:text-base shadow-lg hover:shadow-xl"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToPage(service.path);
                    }}
                  >
                    Learn more
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              variant="outline"
              animation="shake"
              onClick={() => navigateToPage('/services/workflow-automation#what-we-offer')}
              className="px-8 md:px-10 py-3 md:py-4 text-base md:text-lg border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              iconName="ChevronRight"
              iconPosition="right"
              size="lg"
            >
              View All Automation Services
            </Button>
          </div>
        </div>
      </section>

      {/* AI/ML Development Process Timeline */}
      <section id="ai-ml-process" className="py-12 md:py-20 bg-white">
        <span id="process" className="sr-only" aria-hidden="true" />
        <span id="development-timeline" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">From Strategy to Production LLMOps</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From use-case scoring to RAG/agents, evals, and LLMOps, our process is built for accuracy, safety, and business impact.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-3 sm:left-8 top-0 bottom-0 w-0.5 bg-[#00d690]"></div>

              {aiMLDevelopmentTimeline.map((step, index) => (
                <div key={index} className="relative flex items-start mb-8 sm:mb-12">
                  {/* Timeline Dot */}
                  <div className="absolute left-1.5 sm:left-6 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-[#00d690] rounded-full border-4 border-white shadow-lg"></div>

                  {/* Content */}
                  <div className="ml-8 sm:ml-16 bg-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow w-full min-w-0">
                    <div className="flex items-start sm:items-center mb-3 sm:mb-4 gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center text-lg sm:text-xl flex-shrink-0">
                        {step.icon}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base sm:text-xl font-bold text-gray-800 leading-tight">{step.phase}</h3>
                        <p className="text-[#00d690] font-semibold text-sm sm:text-base">{step.duration}</p>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base leading-relaxed">{step.description}</p>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Key Deliverables:</h4>
                      <div className="flex flex-wrap gap-2">
                        {step.deliverables.map((deliverable, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="py-12 md:py-20 bg-gray-50">
        <span id="expertise" className="sr-only" aria-hidden="true" />
        <span id="comparison" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">Why Choose OctalChip</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left Side - Cards Grid */}
            <div className="lg:w-1/2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden"
                    style={{
                      borderRadius: '12px'
                    }}
                  >
                    {/* Gradient overlay on hover */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                      style={{
                        background: 'linear-gradient(45deg, #00dc88, #00bcd4)',
                        borderRadius: '14px',
                        top: '-2px',
                        left: '-2px',
                        right: '-2px',
                        bottom: '-2px',
                        zIndex: '-1'
                      }}
                    ></div>

                    <div className="relative z-10">
                      <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-white/20 transition-all duration-300">
                        <Icon
                          name={item.icon}
                          size={24}
                          className="text-[#00d690] group-hover:text-white transition-all duration-300"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800 group-hover:text-white mb-3 transition-all duration-300">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 group-hover:text-white/90 text-sm leading-relaxed transition-all duration-300">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="lg:w-1/2">
              <div className="bg-gradient-to-br from-[#00d690]/10 to-[#00d690]/5 rounded-2xl p-8 h-96 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-[#00d690]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon name="Users" size={48} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Our Team at Work</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Your project is delivered by a focused team that prioritizes speed, quality, and measurable outcomes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Mobile Responsive */}
      <section id="testimonials" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight px-1">What Our AI Product Clients Say</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-1 leading-relaxed">
              See what clients say about delivery quality, communication speed, and business impact.
            </p>
          </div>

          {/* Mobile Layout - Simple Grid */}
          <div className="block md:hidden">
            <div className="grid grid-cols-1 gap-4">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <div key={`mobile-${index}`} className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                  {/* Company Logo and Description */}
                  <div className="mb-3">
                    <div className="flex items-center mb-2">
                      <div className="w-6 h-6 bg-[#00d690] rounded-lg flex items-center justify-center mr-2">
                        <span className="text-white font-bold text-xs">
                          {testimonial.companyLogo.charAt(0)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-800 text-sm">
                        {testimonial.company}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 ml-8">
                      {testimonial.companyDescription}
                    </p>
                  </div>

                  {/* Testimonial Quote */}
                  <div className="mb-4">
                    <p className="text-gray-700 leading-relaxed italic text-sm">
                      "{testimonial.description}"
                    </p>
                  </div>

                  {/* Client Info */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm">
                        {testimonial.name}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {testimonial.jobTitle} at {testimonial.company}
                      </p>
                      {/* Rating */}
                      <div className="flex mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={12}
                            className={`mr-0.5 ${i < testimonial.rating ? 'text-[#00d690] fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="text-center mt-6">
              <Button
                variant="primary"
                animation="pulse"
                onClick={handleContactForm}
                className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-2 text-sm font-semibold"
              >
                Claim My Free Strategy Plan
              </Button>
            </div>
          </div>

          {/* Desktop Layout - Infinite Scroll */}
          <div className="hidden md:block">
            <div className="relative overflow-hidden">
              <div
                className="flex"
                style={{
                  transform: `translateX(-${testimonialScrollPosition}px)`,
                  width: `${testimonials.length * 2 * 400}px` // Double width for seamless loop
                }}
                onMouseEnter={() => setIsTestimonialPaused(true)}
                onMouseLeave={() => setIsTestimonialPaused(false)}
              >
                {/* First set of testimonials */}
                {testimonials.map((testimonial, index) => (
                  <div key={`first-${index}`} className="flex-shrink-0 w-[400px] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 h-full border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                      {/* Company Logo and Description */}
                      <div className="mb-4">
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 bg-[#00d690] rounded-lg flex items-center justify-center mr-3">
                            <span className="text-white font-bold text-sm">
                              {testimonial.companyLogo.charAt(0)}
                            </span>
                          </div>
                          <span className="font-semibold text-gray-800 text-lg">
                            {testimonial.company}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 ml-11">
                          {testimonial.companyDescription}
                        </p>
                      </div>

                      {/* Testimonial Quote */}
                      <div className="mb-6">
                        <p className="text-gray-700 leading-relaxed italic">
                          "{testimonial.description}"
                        </p>
                      </div>

                      {/* Client Info */}
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                          {testimonial.avatar}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">
                            {testimonial.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {testimonial.jobTitle} at {testimonial.company}
                          </p>
                          {/* Rating */}
                          <div className="flex mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Icon
                                key={i}
                                name="Star"
                                size={16}
                                className={`mr-1 ${i < testimonial.rating ? 'text-[#00d690] fill-current' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Duplicate set for seamless loop */}
                {testimonials.map((testimonial, index) => (
                  <div key={`second-${index}`} className="flex-shrink-0 w-[400px] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 h-full border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                      {/* Company Logo and Description */}
                      <div className="mb-4">
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 bg-[#00d690] rounded-lg flex items-center justify-center mr-3">
                            <span className="text-white font-bold text-sm">
                              {testimonial.companyLogo.charAt(0)}
                            </span>
                          </div>
                          <span className="font-semibold text-gray-800 text-lg">
                            {testimonial.company}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 ml-11">
                          {testimonial.companyDescription}
                        </p>
                      </div>

                      {/* Testimonial Quote */}
                      <div className="mb-6">
                        <p className="text-gray-700 leading-relaxed italic">
                          "{testimonial.description}"
                        </p>
                      </div>

                      {/* Client Info */}
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                          {testimonial.avatar}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">
                            {testimonial.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {testimonial.jobTitle} at {testimonial.company}
                          </p>
                          {/* Rating */}
                          <div className="flex mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Icon
                                key={i}
                                name="Star"
                                size={16}
                                className={`mr-1 ${i < testimonial.rating ? 'text-[#00d690] fill-current' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Reviews Section */}
      <section id="trust-reviews" className="py-10 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#00d690]/5 to-[#00d690]/10">
        <div className="container mx-auto px-4 sm:px-5 md:px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-6 sm:mb-8 md:mb-10 lg:mb-12 px-1 leading-tight">
              Trusted by Companies Shipping Intelligent Products
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-8 items-stretch">
              {/* Clutch.co Column */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 md:p-8 lg:p-10 xl:p-12 shadow-lg hover:shadow-xl transition-all duration-300 min-h-[300px] sm:min-h-[320px] flex flex-col">
                <div className="flex flex-col items-center justify-between gap-4 sm:gap-5 md:gap-6 flex-1">
                  {/* Clutch Logo and Rating */}
                  <div className="text-center flex-shrink-0">
                    <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-1.5 sm:mb-2">
                      Clutch
                      <span className="inline-block w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full ml-1 align-middle"></span>
                    </div>
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                      <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">4.9</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={24}
                            className={`w-5 h-5 sm:w-6 sm:h-6 mx-0.5 flex-shrink-0 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Trust Statement */}
                  <div className="text-center flex-1 min-w-0">
                    <p className="text-lg sm:text-xl md:text-2xl text-gray-800 font-semibold mb-2 sm:mb-4 leading-snug">
                      Hundreds of companies trust OctalChip for AI delivery
                    </p>
                    <p className="text-gray-600 text-base sm:text-lg mb-4 sm:mb-6">
                      Verified reviews from real clients on Clutch.co
                    </p>
                  </div>

                  {/* CTA Button */}
                  <div className="flex-shrink-0 w-full flex justify-center">
                    <Button
                      variant="primary"
                      animation="glow"
                      onClick={() => window.open('https://clutch.co/profile/octalchip#reviews', '_blank')}
                      className="inline-flex items-center justify-center bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-2.5 px-4 sm:py-3 sm:px-6 text-sm sm:text-base min-h-[44px] sm:min-h-[48px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto"
                      iconName="ExternalLink"
                      iconPosition="left"
                    >
                      View Clutch.co Profile
                    </Button>
                  </div>
                </div>
              </div>

              {/* Trustpilot Column */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 md:p-8 lg:p-10 xl:p-12 shadow-lg hover:shadow-xl transition-all duration-300 min-h-[300px] sm:min-h-[320px] flex flex-col">
                <div className="flex flex-col items-center justify-between gap-4 sm:gap-5 md:gap-6 flex-1">
                  {/* Trustpilot Logo and Rating */}
                  <div className="text-center flex-shrink-0">
                    <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-1.5 sm:mb-2 flex items-center justify-center gap-1.5 sm:gap-2 flex-nowrap">
                      <Icon
                        name="Star"
                        size={40}
                        className="text-[#00d690] fill-current w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex-shrink-0 inline-block align-middle"
                      />
                      <span className="whitespace-nowrap">Trustpilot</span>
                    </div>
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                      <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">4.7</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={24}
                            className={`w-5 h-5 sm:w-6 sm:h-6 mx-0.5 flex-shrink-0 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Trust Statement */}
                  <div className="text-center flex-1 min-w-0">
                    <p className="text-lg sm:text-xl md:text-2xl text-gray-800 font-semibold mb-2 sm:mb-4 leading-snug">
                      Real customer experiences and verified reviews
                    </p>
                    <p className="text-gray-600 text-base sm:text-lg mb-4 sm:mb-6">
                      See what our clients say about our services
                    </p>
                  </div>

                  {/* CTA Button */}
                  <div className="flex-shrink-0 w-full flex justify-center">
                    <Button
                      variant="primary"
                      animation="pulse"
                      onClick={() => window.open('https://www.trustpilot.com/review/octalchip.com', '_blank')}
                      className="inline-flex items-center justify-center bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-2.5 px-4 sm:py-3 sm:px-6 text-sm sm:text-base min-h-[44px] sm:min-h-[48px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto"
                      iconName="ExternalLink"
                      iconPosition="left"
                    >
                      View Trustpilot Reviews
                    </Button>
                  </div>
                </div>
              </div>

              {/* G2 Column - centered when 2-col (tablet), full width when 3-col (desktop) */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 md:p-8 lg:p-10 xl:p-12 shadow-lg hover:shadow-xl transition-all duration-300 min-h-[300px] sm:min-h-[320px] flex flex-col md:col-span-2 xl:col-span-1 md:max-w-[28rem] xl:max-w-none md:mx-auto xl:mx-0">
                <div className="flex flex-col items-center justify-between gap-4 sm:gap-5 md:gap-6 flex-1 md:w-full">
                  {/* G2 Logo and Rating */}
                  <div className="text-center flex-shrink-0">
                    <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-1.5 sm:mb-2 flex items-center justify-center gap-2">
                      G2
                    </div>
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                      <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">4.5</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={24}
                            className={`w-5 h-5 sm:w-6 sm:h-6 mx-0.5 flex-shrink-0 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Trust Statement */}
                  <div className="text-center flex-1 min-w-0">
                    <p className="text-lg sm:text-xl md:text-2xl text-gray-800 font-semibold mb-2 sm:mb-4 leading-snug">
                      Comprehensive software reviews and ratings
                    </p>
                    <p className="text-gray-600 text-base sm:text-lg mb-4 sm:mb-6">
                      Read authentic reviews from verified users on G2
                    </p>
                  </div>

                  {/* CTA Button */}
                  <div className="flex-shrink-0 w-full flex justify-center">
                    <Button
                      variant="primary"
                      animation="wiggle"
                      onClick={() => window.open('https://www.g2.com/products/octalchip/reviews', '_blank')}
                      className="inline-flex items-center justify-center bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-2.5 px-4 sm:py-3 sm:px-6 text-sm sm:text-base min-h-[44px] sm:min-h-[48px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto"
                      iconName="ExternalLink"
                      iconPosition="left"
                    >
                      View G2 Reviews
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Blogs Section */}
      <section id="recent-blogs" className="py-12 md:py-20 bg-white">
        <span id="top-stories" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">Latest AI Insights & Guides</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Get practical insights on generative AI, agents, RAG, and AI product delivery you can apply now.
            </p>
          </div>

          {isLoadingBlogs ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#00d690]"></div>
              <p className="mt-4 text-gray-600">Loading latest articles...</p>
            </div>
          ) : recentBlogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {recentBlogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden border border-gray-100"
                  style={{ borderRadius: '12px' }}
                >

                  <div className="p-6">
                    {/* Category and Read Time */}
                    <div className="flex gap-2 mb-4 flex-wrap">
                      <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {blog.category}
                      </span>
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                        {blog.readTime}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors line-clamp-2">
                      {blog.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {blog.shortDescription}
                    </p>

                    {/* Date and Read Time */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={16} />
                        <span>{formatDate(blog.publishDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={16} />
                        <span>{blog.readTime}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex gap-1 mb-4 flex-wrap">
                      {blog.tags.slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                      {blog.tags.length > 2 && (
                        <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs font-medium">
                          +{blog.tags.length - 2} more
                        </span>
                      )}
                    </div>

                    {/* Read More Button */}
                    <div className="text-center">
                      <span className="inline-flex items-center text-[#00d690] font-semibold text-sm group-hover:text-[#00b876] transition-colors">
                        Read More
                        <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="BookOpen" size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No articles available at the moment.</p>
            </div>
          )}

          {/* View All Blogs Button */}
          <div className="text-center">
            <Button
              variant="primary"
              animation="rotate"
              onClick={() => navigate('/blog')}
              className="inline-flex items-center bg-[#00d690] hover:bg-[#00b876] text-white font-semibold py-3 px-8 transition-all duration-300 transform hover:scale-105"
              iconName="BookOpen"
              iconPosition="left"
            >
              View All Articles
            </Button>
          </div>
        </div>
      </section>

      

      {/* Industries Section */}
      <section id="industries" className="py-12 md:py-20 bg-white">
        <span id="industry-expertise" className="sr-only" aria-hidden="true" />
        <span id="global-reach" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight px-1">Industries We Serve</h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Vertical AI solutions for fintech, healthcare, manufacturing, real estate, and more.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 md:gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon name={industry.icon} size={32} />
                </div>
                <h3 className="font-semibold text-text-primary text-sm sm:text-base leading-snug px-1">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      

      {/* Case Studies Section */}
      <section id="case-studies" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight px-1">AI Success Stories & Case Studies</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              How teams ship generative AI, agents, and intelligent systems with measurable growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 lg:gap-12">
            {caseStudies.map((caseStudy, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="p-4 sm:p-6 md:p-8">
                  <div className="flex items-start sm:items-center mb-4 sm:mb-6 gap-3">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0 text-2xl sm:text-3xl">
                      {caseStudy.image}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base sm:text-xl font-bold text-gray-800 leading-tight">{caseStudy.title}</h3>
                      <p className="text-gray-600 text-sm sm:text-base">{caseStudy.client} • {caseStudy.industry}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs sm:text-sm text-gray-500">
                        <span><Icon name="Clock" size={16} className="inline mr-1" /> {caseStudy.duration}</span>
                        <span><Icon name="Users" size={16} className="inline mr-1" /> {caseStudy.team}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-2">Challenge:</h4>
                    <p className="text-gray-600 mb-4">{caseStudy.challenge}</p>

                    <h4 className="font-semibold text-gray-800 mb-2">Solution:</h4>
                    <p className="text-gray-600 mb-4">{caseStudy.solution}</p>
                  </div>

                  <div className="mb-4 sm:mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Results:</h4>
                    <ul className="space-y-2">
                      {caseStudy.results.map((result, idx) => (
                        <li key={idx} className="flex items-start text-gray-600 text-sm sm:text-base pr-1">
                          <Icon name="CheckCircle" size={16} className="text-[#00d690] mr-2 flex-shrink-0 mt-0.5" />
                          <span className="min-w-0 leading-relaxed">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {caseStudy.technologies.map((tech, idx) => (
                      <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      

      



      {/* FAQ Section */}
      <FAQ 
        faqs={homepageFAQs}
        title="Frequently Asked Questions"
        description="Get clear answers on generative AI, agents, RAG, delivery process, and expected outcomes."
      />

      

      {/* Our SaaS Products Section */}
      <section id="saas-products" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">Our SaaS Products</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Explore SaaS products that pair with our AI engineering work to improve productivity and measurable results.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-[#00d690]/5 to-blue-500/5 rounded-2xl p-8 shadow-lg">
              <div className="grid md:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center">
                      <Icon name="Mail" size={24} className="text-[#00d690]" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">Email Validator SaaS</h3>
                      <p className="text-[#00d690] font-semibold">Professional Email Validation Service</p>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Validate single emails or bulk lists with high accuracy to reduce bounce rates and improve deliverability.
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">99.9%</div>
                      <div className="text-sm text-gray-600">Accuracy Rate</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">Bulk</div>
                      <div className="text-sm text-gray-600">Processing</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">API</div>
                      <div className="text-sm text-gray-600">Integration</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">GDPR</div>
                      <div className="text-sm text-gray-600">Compliant</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Key Features:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Real-time validation
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Bulk processing
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Detailed analytics
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        API access
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="primary"
                      animation="bounce"
                      onClick={() => window.open('https://mailvalidator.octalchip.com/', '_blank')}
                      className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 font-semibold transition-colors text-center"
                    >
                      Try Email Validator
                    </Button>
                    <Button
                      variant="outline"
                      animation="pulse"
                      onClick={() => navigate('/contact')}
                      className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-6 py-3 font-semibold transition-colors text-center"
                    >
                      Get Custom Quote
                    </Button>
                  </div>
                </div>

                <div className="text-center">
                  <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-dashed border-[#00d690]/30">
                    <div className="w-20 h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="Mail" size={40} className="text-[#00d690]" />
                    </div>
                    <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Free Trial Available</h4>
                    <p className="text-gray-600 mb-6">
                      Get 100 free credits to test our email validation service
                    </p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Free Credits:</span>
                        <span className="font-semibold text-[#00d690]">100</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Validation Type:</span>
                        <span className="font-semibold text-gray-800">Single & Bulk</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Support:</span>
                        <span className="font-semibold text-gray-800">Email Support</span>
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      animation="glow"
                      onClick={() => window.open('https://mailvalidator.octalchip.com/', '_blank')}
                      className="w-full bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 font-semibold transition-colors mt-6"
                    >
                      Start Free Trial
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto mt-12 md:mt-16">
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-slate-200/80 overflow-hidden">
              <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-[#00d690]/10 text-[#00d690] text-xs sm:text-sm font-medium border border-[#00d690]/15 shadow-sm mb-5">
                    <Icon name="Sparkles" size={18} className="text-[#00d690]" />
                    <span>Professional sites without the long agency wait</span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">
                    Get a Business Website in Days, Not Months
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Tell us about your business, preview template options, then continue on WhatsApp. We confirm your package and domain there, and deliver your live site quickly with a clear launch schedule.
                  </p>

                  <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-soft mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-3 sm:gap-4 justify-start">
                      <div className="flex items-baseline gap-3">
                        <span className="text-4xl font-bold text-[#00d690]">₹3,999</span>
                        <span className="text-sm text-gray-600">one-time, website and management</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <span className="inline-flex items-center gap-1.5 bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-lg text-xs font-medium">
                        <Icon name="Check" size={14} className="text-[#00d690]" />
                        No monthly website builder fees
                      </span>
                      <span className="inline-flex items-center gap-1.5 bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-lg text-xs font-medium">
                        <Icon name="Check" size={14} className="text-[#00d690]" />
                        Typically ready in a few business days
                      </span>
                      <span className="inline-flex items-center gap-1.5 bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-lg text-xs font-medium">
                        <Icon name="Check" size={14} className="text-[#00d690]" />
                        Domain handled with you on WhatsApp
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    <div className="bg-gray-50 rounded-2xl border border-slate-200/70 p-4 text-center">
                      <div className="w-10 h-10 rounded-xl bg-[#00d690]/10 flex items-center justify-center mx-auto text-[#00d690] font-bold">
                        1
                      </div>
                      <div className="mt-3 font-semibold text-gray-800 text-sm">Submit details</div>
                      <p className="text-xs text-gray-600 mt-1">Business name and description</p>
                    </div>
                    <div className="bg-gray-50 rounded-2xl border border-slate-200/70 p-4 text-center">
                      <div className="w-10 h-10 rounded-xl bg-[#00d690]/10 flex items-center justify-center mx-auto text-[#00d690] font-bold">
                        2
                      </div>
                      <div className="mt-3 font-semibold text-gray-800 text-sm">Pick a template</div>
                      <p className="text-xs text-gray-600 mt-1">Preview and choose your layout</p>
                    </div>
                    <div className="bg-gray-50 rounded-2xl border border-slate-200/70 p-4 text-center">
                      <div className="w-10 h-10 rounded-xl bg-[#00d690]/10 flex items-center justify-center mx-auto text-[#00d690] font-bold">
                        3
                      </div>
                      <div className="mt-3 font-semibold text-gray-800 text-sm">Continue on WhatsApp</div>
                      <p className="text-xs text-gray-600 mt-1">Domain, payment, and go-live</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="primary"
                      animation="bounce"
                      onClick={() => window.open('https://quicksite.octalchip.com/', '_blank')}
                      className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 font-semibold transition-colors text-center"
                    >
                      Start on QuickSite
                    </Button>
                    <Button
                      variant="outline"
                      animation="pulse"
                      onClick={() => navigate('/contact')}
                      className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-6 py-3 font-semibold transition-colors text-center"
                    >
                      Contact for custom build
                    </Button>
                  </div>
                </div>

                <div className="text-center">
                  <div className="h-full bg-[#00d690]/5 rounded-3xl p-7 sm:p-8 border border-[#00d690]/20 flex flex-col justify-between">
                    <div>
                      <div className="w-16 h-16 rounded-2xl bg-white border border-[#00d690]/20 flex items-center justify-center mx-auto mb-5">
                        <Icon name="Globe" size={36} className="text-[#00d690]" />
                      </div>
                      <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-2">
                        Website build, hosting &amp; ongoing support
                      </h4>
                      <p className="text-gray-600 mb-5">
                        Templates, hosting, and SSL are included. After go-live, you can request updates through developer support.
                      </p>

                      <div className="flex flex-wrap justify-center gap-2 mb-6">
                        <span className="bg-white/80 border border-slate-200 px-3 py-1 rounded-full text-xs text-gray-700 font-medium">
                          Hosting
                        </span>
                        <span className="bg-white/80 border border-slate-200 px-3 py-1 rounded-full text-xs text-gray-700 font-medium">
                          SSL
                        </span>
                        <span className="bg-white/80 border border-slate-200 px-3 py-1 rounded-full text-xs text-gray-700 font-medium">
                          Templates
                        </span>
                        <span className="bg-white/80 border border-slate-200 px-3 py-1 rounded-full text-xs text-gray-700 font-medium">
                          WhatsApp-led setup
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="primary"
                      animation="glow"
                      onClick={() => window.open('https://quicksite.octalchip.com/', '_blank')}
                      className="w-full bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 font-semibold transition-colors"
                    >
                      Open QuickSite
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section id="technology-stack" className="py-12 md:py-20 bg-white">
        <span id="skills" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">Our Technology Stack</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We use modern LLMs, agent frameworks, vector search, and cloud platforms to build AI products that accelerate growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">AI Product Frontend Technologies</h3>
              <div className="space-y-6">
                {frontendTechnologies.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-semibold text-gray-800">{item.tech}</h4>
                      <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs font-medium">
                        {item.projects} projects
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.benefits.map((benefit, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">AI Backend, Orchestration & Cloud Infrastructure</h3>
              <div className="space-y-6">
                {backendTechnologies.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-semibold text-gray-800">{item.tech}</h4>
                      <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs font-medium">
                        {item.projects} projects
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.benefits.map((benefit, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">AI & Machine Learning Technologies & Platforms</h3>
              <div className="space-y-6">
                {aiMLTechnologies.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-semibold text-gray-800">{item.tech}</h4>
                      <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs font-medium">
                        {item.projects} projects
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.benefits.map((benefit, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Want to learn more about our technology choices?</p>
            <Button
              variant="primary"
              animation="float"
              className="bg-[#00d690] hover:bg-[#00b876] text-white font-semibold py-3 px-8"
              onClick={() => navigateToPage('/services')}
            >
              View Our Tech Stack
            </Button>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section id="security-compliance" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">Data Security & Compliance</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We implement strong security and compliance controls so your product stays protected, audit-ready, and trusted as you scale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {securityFeatures.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                <p className="text-[#00d690] text-xs font-medium">{item.details}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Compliance & Certifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="FileText" size={24} className="text-gray-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">GDPR Compliant</h4>
                <p className="text-gray-600 text-sm">European data protection standards</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Building" size={24} className="text-gray-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">SOC 2 Type II</h4>
                <p className="text-gray-600 text-sm">Security and availability controls</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Lock" size={24} className="text-gray-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">ISO 27001</h4>
                <p className="text-gray-600 text-sm">Information security management</p>
              </div>
            </div>
            <div className="text-center mt-8">
              <Button
                variant="outline"
                animation="glow"
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-semibold px-6 py-3"
                onClick={() => window.open('https://cal.com/octalchip/30min', '_blank')}
              >
                Walk Through Data and Compliance Needs
              </Button>
            </div>
          </div>
        </div>
      </section>

      

      

      

      

      

      

      

      

      {/* Final CTA Section */}
      <section id="contact" className="bg-slate-100 py-12 sm:py-16 md:py-20">
        <span id="calculator" className="sr-only" aria-hidden="true" />
        <span id="faq" className="sr-only" aria-hidden="true" />
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-4 sm:mb-6 leading-tight">
              Want Faster Growth With AI Agents & Automation?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-slate-500 mb-6 sm:mb-8 leading-relaxed">
              Share your goals and get a practical roadmap to launch faster, improve conversions, and scale with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
              <Button
                variant="outline"
                animation="glow"
                className="border-primary text-primary bg-white hover:bg-primary hover:text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold"
                onClick={() => window.open('https://cal.com/octalchip/30min', '_blank')}
              >
                Claim Free Strategy Call
              </Button>
              <Button
                variant="primary"
                animation="float"
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg"
                onClick={() => navigateToPage('/services')}
              >
                Compare Growth Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home; 